import { useEffect, useId, useMemo, useRef, useState, type MutableRefObject } from 'react'
import { createPortal } from 'react-dom'
import type { Editor } from '@tiptap/react'
import type { Transaction } from '@tiptap/pm/state'
import type * as Y from 'yjs'
import type { Awareness } from 'y-protocols/awareness'
import { useLang } from '../i18n'
import { navigate, roomPath } from '../lib/router'
import type { PartKind } from '../lib/types'
import { findInDoc, withinRange, currentSectionRange, replaceMatchesInEditor, type PmMatch } from './editor/find-in-editor'
import { setFindHighlight } from './editor/find-highlight'
import { makeHistoryEntry, appendHistory } from './editor/history-log'
import { collectTargets, applyWideReplace, type PartTarget } from '../lib/wide-replace'
import { useFindStore, type FindScope, type WideRun } from '../lib/findStore'
import { artefactOf, globalIndex, partCounts, stepRoom, sumByArtefact, totalOf, type ArtefactSum } from '../lib/find-nav'
import { ConfirmModal } from './ConfirmModal'
// Types only: the deck's code is the lazily loaded editor's chunk; `deck` brings its matcher along.
import type { DeckFindApi, DeckMatch } from '../slides/find'

export type { FindScope } from '../lib/findStore'

/** How long typing settles before a wide scope counts again (it opens every part of the scope). */
export const WIDE_COUNT_DEBOUNCE_MS = 300
/** How long a part Find opened gets to show a match once synced before the panel stops waiting
 *  (a teammate removed them since the count, or they only exist in the raw markdown). */
export const PENDING_GIVE_UP_MS = 2000

interface FindReplaceProps {
  /** Ctrl+F opens 'find' (no replacement field); Ctrl+H opens 'replace'. A panel already open
   *  just switches mode in place — see Canvas.tsx's keydown handler. */
  mode: 'find' | 'replace'
  /** Each shortcut press hands a new seed (AB#330): the panel takes the member's selected text as
   *  the query when there is one, and puts the caret back in the search field. */
  seed?: { text: string; n: number }
  room: string
  kind: PartKind
  /** The open part's live editor, null until RichEditor mounts (and forever on a grid part —
   *  GridEditor never sets it). */
  editor: Editor | null
  ydoc: Y.Doc
  ytext: Y.Text
  /** A slides part: the open deck's find surface (SlidesEditor hands it up; null until it mounts). */
  deck?: DeckFindApi | null
  /** The open room's awareness: who holds which element of a deck, for the wide scopes. */
  awareness?: Awareness | null
  /** The open room has synced: a part Find opened is only searched for its pending match then. */
  synced?: boolean
  wsUrl: string
  actorName: string
  readOnly: boolean
  /** Suppresses useHistoryCapture's own generic logging while this panel writes directly to the
   *  OPEN room (locally, or via the wide scope's `open` reuse) — the same guard RichEditor's
   *  "improve with AI" flow uses, so this panel can log its own descriptive entry instead. */
  applyingRef: MutableRefObject<boolean>
  onClose: () => void
}

/** Two deck matches naming the same occurrence. */
const sameDeckMatch = (a: DeckMatch | null, b: DeckMatch | null): boolean =>
  a === b || (!!a && !!b && JSON.stringify(a) === JSON.stringify(b))

/** An artefact's results-row fragment: found (a count) or replaced (a report), then what was left
 *  alone. `t`'s type is borrowed from `useLang()` — the dictionary's keys are a closed union. */
function sumLabel(t: ReturnType<typeof useLang>['t'], s: ArtefactSum): string {
  const parts: string[] = []
  if (s.count) parts.push(t(s.replaced ? 'fr_result_replaced' : 'fr_result_matched').replace('{n}', String(s.count)))
  if (s.skipped) parts.push(t('fr_result_skipped').replace('{n}', String(s.skipped)))
  if (s.locked) parts.push(t('fr_result_locked'))
  if (s.failed) parts.push(t('fr_result_failed'))
  return parts.length ? parts.join(', ') : t('fr_result_unchanged')
}

/**
 * Find/Replace for the document editor — Ctrl+F / Ctrl+H (Canvas.tsx owns the shortcuts and the
 * open/mode state; this component is mounted only while the panel is open).
 *
 * The open part is always searched by its own editor: the live editor's rendered text
 * (components/editor/find-in-editor.ts), or a deck's matcher (slides/find.ts, through `deck`, which
 * selects the match's element and honours the deck's locks). That is what ↑/↓ highlight and what
 * Replace / the local Replace all write through — real transactions, so the editor's § lock
 * enforcement and markdown→Y.Text sync apply and nothing here duplicates them.
 *
 * "This artefact" (the default) / "all artefacts" are continuous (AB#366): every OTHER part of the
 * scope is counted in a background room (lib/wide-replace.ts, the open one reused), and ↑/↓ step
 * through the scope's matches in order (lib/find-nav.ts) — past the open part's last match, the next
 * part holding one opens and its first match is selected. The panel's state lives in a store
 * (lib/findStore.ts) so it survives that room change: Canvas keeps it open over a room Find opened,
 * and it re-binds to that room's editor once synced. The results are one row per artefact; a row
 * opens the artefact at its first match. The wide Replace all edits every part's Y.Text directly
 * (after a confirmation), one history entry per part it changes.
 */
export function FindReplace({
  mode,
  seed,
  room,
  kind,
  editor,
  ydoc,
  ytext,
  deck = null,
  awareness = null,
  synced = true,
  wsUrl,
  actorName,
  readOnly,
  applyingRef,
  onClose,
}: FindReplaceProps) {
  const { t } = useLang()
  const titleId = useId()
  const isGrid = kind === 'grid'
  const isSlides = kind === 'slides'
  const canEdit = !readOnly
  const bid = room.split('/')[0] ?? null

  // Resumed: Find itself opened this room (`pending`), so the session goes on; otherwise a fresh one.
  const [init] = useState(() => {
    const s = useFindStore.getState()
    const resumed = s.pending?.room === room && s.bid === bid
    return resumed
      ? { resumed, query: s.query, replacement: s.replacement, caseSensitive: s.caseSensitive, scope: s.scope, count: s.count, report: s.report }
      : { resumed, query: seed?.text ?? '', replacement: '', caseSensitive: false, scope: 'artefact' as FindScope, count: null, report: null }
  })
  const [scope, setScope] = useState<FindScope>(init.scope)
  const [query, setQuery] = useState(init.query)
  const [replacement, setReplacement] = useState(init.replacement)
  const [caseSensitive, setCaseSensitive] = useState(init.caseSensitive)
  const [activeIndex, setActiveIndex] = useState(0)
  // Bumped only on a DOC-CHANGING transaction (never a selection-only one — see the effect below
  // that navigates to a match, which would otherwise feed back into this and loop).
  const [tick, setTick] = useState(0)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  const [count, setCount] = useState<WideRun | null>(init.count)
  const [countRun, setCountRun] = useState(0)
  const [report, setReport] = useState<WideRun | null>(init.report)
  const [pendingTargets, setPendingTargets] = useState<PartTarget[] | null>(null)
  const pending = useFindStore((s) => s.pending)

  const inputRef = useRef<HTMLInputElement>(null)
  const isLocalScope = scope === 'section' || scope === 'slide' || scope === 'part'
  const isWideScope = !isLocalScope

  // The session, kept for the room change Find causes. A fresh session drops a stale navigation.
  useEffect(() => {
    if (!init.resumed) useFindStore.getState().clearPending()
  }, [init])
  useEffect(() => {
    useFindStore.getState().save({ bid, query, replacement, caseSensitive, scope, count, report })
  }, [bid, query, replacement, caseSensitive, scope, count, report])

  // Focus the search field on open; restore focus to whatever had it (the editor, a toolbar
  // button) on close — same pattern as ConfirmModal/PromptModal.
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null
    inputRef.current?.focus()
    inputRef.current?.select()
    return () => prev?.focus()
  }, [])
  // A shortcut pressed again while the panel is open: same as opening it afresh.
  const seedN = useRef(seed?.n)
  useEffect(() => {
    if (!seed || seed.n === seedN.current) return
    seedN.current = seed.n
    if (seed.text) setQuery(seed.text)
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [seed])

  // Recompute local matches on a content change from any OTHER source (a keystroke elsewhere, a
  // remote/AI edit landing, our own Replace/Replace All) — never on the selection-only transaction
  // this component dispatches to navigate to a match.
  useEffect(() => {
    if (isSlides || !editor || editor.isDestroyed) return
    let timer: ReturnType<typeof setTimeout> | undefined
    const onTx = ({ transaction }: { transaction: Transaction }): void => {
      if (!transaction.docChanged) return
      clearTimeout(timer)
      timer = setTimeout(() => setTick((v) => v + 1), 150)
    }
    editor.on('transaction', onTx)
    return () => {
      clearTimeout(timer)
      editor.off('transaction', onTx)
    }
  }, [editor, isSlides])
  // A deck: any change to the Y.Doc (a keystroke, a teammate, our own Replace) may move the matches.
  useEffect(() => {
    if (!isSlides) return
    let timer: ReturnType<typeof setTimeout> | undefined
    const onUpdate = (): void => {
      clearTimeout(timer)
      timer = setTimeout(() => setTick((v) => v + 1), 150)
    }
    ydoc.on('update', onUpdate)
    return () => {
      clearTimeout(timer)
      ydoc.off('update', onUpdate)
    }
  }, [ydoc, isSlides])

  // The open part's matches: the local scope's, or — in a wide scope — the whole part's.
  /** "This slide" follows the slide on screen; the other scopes do not depend on it. */
  const deckSlide = scope === 'slide' ? (deck?.current ?? null) : null
  const deckMatches = useMemo<DeckMatch[]>(() => {
    if (!isSlides || !deck || (scope === 'slide' && !deckSlide)) return []
    return deck.find(query, { caseSensitive }, deckSlide ? [deckSlide] : undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `tick` stands in for "the doc may have changed"
  }, [isSlides, deck, scope, deckSlide, query, caseSensitive, tick])

  const pmMatches = useMemo<PmMatch[]>(() => {
    if (isSlides || !editor || editor.isDestroyed) return []
    const all = findInDoc(editor.state.doc, query, caseSensitive)
    if (scope !== 'section') return all
    const range = currentSectionRange(editor.state.doc, editor.state.selection.from)
    return range ? withinRange(all, range.from, range.to) : all
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `tick` stands in for "the doc may have changed"
  }, [editor, isSlides, scope, query, caseSensitive, tick])

  const matchCount = isSlides ? deckMatches.length : pmMatches.length
  const clampedIndex = matchCount ? ((activeIndex % matchCount) + matchCount) % matchCount : 0

  // ── the wide scope: its count, and where the open part's matches sit in it ──────────────────
  const scopeRoot = scope === 'artefact' ? `${bid}/${artefactOf(room)}` : bid
  const wideKey = isWideScope && query !== '' ? JSON.stringify([scope, caseSensitive, scopeRoot, query]) : null
  const wide = count && count.key === wideKey ? count : null
  const shownReport = report && report.key === wideKey ? report : null
  /** The open part can be searched by its own editor: synced, and the editor (or deck) mounted. */
  const ready = synced && (isSlides ? !!deck : !isGrid && !!editor && !editor.isDestroyed)
  const pendingHere = pending && pending.room === room ? pending : null
  const live = ready && !pendingHere ? { room, count: matchCount } : undefined
  const counts = useMemo(() => (wide ? partCounts(wide.targets, wide.outcomes, live) : null), [wide, live?.room, live?.count]) // eslint-disable-line react-hooks/exhaustive-deps
  const total = counts ? totalOf(counts) : null
  const position = counts && matchCount && !pendingHere ? globalIndex(counts, room, clampedIndex) : null

  // Count the scope as the query settles (and afresh on a re-bind, and after a Replace all).
  useEffect(() => {
    if (!wideKey) return
    let current = true
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const targets = await collectTargets(room, scope as 'artefact' | 'bid')
          if (!current) return
          setProgress({ done: 0, total: targets.length })
          const outcomes = await applyWideReplace({
            targets,
            query,
            replacement: null,
            options: { caseSensitive },
            wsUrl,
            actorName,
            historyPrompt: '',
            open: { room, ydoc, ytext, awareness },
            onProgress: (done, n) => {
              if (current) setProgress({ done, total: n })
            },
          })
          if (current) setCount({ key: wideKey, targets, outcomes })
        } catch {
          // The count stays unknown: ↑/↓ still go through the open part.
        } finally {
          if (current) setProgress(null)
        }
      })()
    }, WIDE_COUNT_DEBOUNCE_MS)
    return () => {
      current = false
      clearTimeout(timer)
      setProgress(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `wideKey` carries scope/case/query/root; a room change remounts the panel
  }, [wideKey, countRun])

  // Restart navigation at the first match whenever the query/options/scope change underneath it.
  useEffect(() => {
    setActiveIndex(0)
  }, [query, caseSensitive, scope])

  // A part Find opened: once it can be searched, select the match it was opened for.
  useEffect(() => {
    if (!pendingHere || !ready) return
    if (matchCount > 0) {
      setActiveIndex(pendingHere.at === 'first' ? 0 : matchCount - 1)
      useFindStore.getState().clearPending()
      return
    }
    const timer = setTimeout(() => {
      useFindStore.getState().clearPending()
      setCountRun((n) => n + 1)
    }, PENDING_GIVE_UP_MS)
    return () => clearTimeout(timer)
  }, [pendingHere, ready, matchCount])

  // Highlight the current match set and put the caret on the active one — a decoration, not a
  // native selection alone, so it stays visible while keyboard focus sits in this panel's inputs.
  useEffect(() => {
    if (isSlides || !editor || editor.isDestroyed) return
    if (pmMatches.length === 0) {
      setFindHighlight(editor, [], -1)
      return
    }
    setFindHighlight(editor, pmMatches, clampedIndex)
    const m = pmMatches[clampedIndex]!
    editor.chain().setTextSelection(m).scrollIntoView().run()
  }, [editor, isSlides, pmMatches, clampedIndex])

  // A deck: go to the active match (its slide, its element, the range) — only when it is another
  // occurrence, so a teammate's keystroke re-finding the same one does not pull the member back.
  const shown = useRef<DeckMatch | null>(null)
  useEffect(() => {
    if (!isSlides || !deck) return
    const m = deckMatches[clampedIndex] ?? null
    if (sameDeckMatch(m, shown.current)) return
    shown.current = m
    deck.show(m)
  }, [isSlides, deck, deckMatches, clampedIndex])

  // Clear the highlight on close/unmount — it must not outlive this panel.
  useEffect(
    () => () => {
      if (editor && !editor.isDestroyed) setFindHighlight(editor, [], -1)
    },
    [editor],
  )
  // The deck's api is handed up afresh as its slide changes: only closing the panel clears it.
  const deckRef = useRef(deck)
  deckRef.current = deck
  useEffect(() => () => deckRef.current?.show(null), [])

  function historyPrompt(): string {
    return t('fr_history_prompt').replace('{q}', query).replace('{r}', replacement)
  }

  function close(): void {
    useFindStore.getState().clearPending()
    onClose()
  }

  /** Open another part of the scope at its first or last match: the panel follows (Canvas keeps it
   *  open over a room change Find caused) and selects it once that room has synced. */
  function goTo(target: string, at: 'first' | 'last'): void {
    useFindStore.getState().save({ bid, query, replacement, caseSensitive, scope, count, report, pending: { room: target, at } })
    navigate(roomPath(target))
  }

  /** ↓ (1) / ↑ (-1): the open part's next match; past its last, the scope's next part holding one. */
  function step(dir: 1 | -1): void {
    if (isLocalScope || !counts) {
      if (matchCount) setActiveIndex((i) => i + dir)
      return
    }
    if (pendingHere) return
    if (matchCount && (dir === 1 ? clampedIndex < matchCount - 1 : clampedIndex > 0)) {
      setActiveIndex((i) => i + dir)
      return
    }
    const next = stepRoom(counts, room, dir)
    if (next === null) return
    if (next === room) setActiveIndex(dir === 1 ? 0 : matchCount - 1)
    else goTo(next, dir === 1 ? 'first' : 'last')
  }

  /** A results row: that artefact, at its first match. */
  function openArtefact(artefact: string): void {
    const first = counts?.find((c) => c.artefact === artefact && c.count > 0)
    if (!first) return
    if (first.room === room) setActiveIndex(0)
    else goTo(first.room, 'first')
  }

  /** Replace these deck matches through the open deck: what another holds is skipped, and said. */
  function applyDeck(matches: DeckMatch[]): void {
    if (!deck || matches.length === 0) return
    const done = deck.replace(matches, replacement, (n) => t('fr_history_prompt_n').replace('{q}', query).replace('{r}', replacement).replace('{n}', String(n)))
    // The text changed under the shown match: the one found next is shown afresh.
    shown.current = null
    setNotice(done.skipped ? t('fr_skipped_locked').replace('{n}', String(done.skipped)).replace('{names}', done.holders.join(', ')) : null)
  }

  /** Replace the given local matches through the editor, then log (or, if the write landed in a
   *  section someone else holds and was rejected outright, say so instead of pretending it ran). */
  function applyLocal(matches: PmMatch[]): void {
    if (!editor || editor.isDestroyed || matches.length === 0) return
    const before = ytext.toString()
    applyingRef.current = true
    try {
      replaceMatchesInEditor(editor, matches, replacement)
    } finally {
      applyingRef.current = false
    }
    const after = ytext.toString()
    if (before === after) {
      setNotice(t('sec_lock_deny'))
      return
    }
    setNotice(null)
    const entry = makeHistoryEntry({ actor: actorName, kind: 'human', before, after, prompt: historyPrompt() })
    if (entry) appendHistory(ydoc, entry)
  }

  /** Replace the current match — in a wide scope too: it is always in the open part. */
  function replaceOne(): void {
    if (!canEdit || matchCount === 0) return
    if (isSlides) applyDeck([deckMatches[clampedIndex]!])
    else applyLocal([pmMatches[clampedIndex]!])
  }

  function replaceAllLocal(): void {
    if (!canEdit || matchCount === 0) return
    if (isSlides) applyDeck(deckMatches)
    else applyLocal(pmMatches)
  }

  async function replaceWide(targets: PartTarget[]): Promise<void> {
    const key = wideKey
    setBusy(true)
    setReport(null)
    setProgress({ done: 0, total: targets.length })
    applyingRef.current = true
    try {
      const outcomes = await applyWideReplace({
        targets,
        query,
        replacement,
        options: { caseSensitive },
        wsUrl,
        actorName,
        historyPrompt: historyPrompt(),
        open: { room, ydoc, ytext, awareness },
        onProgress: (done, n) => setProgress({ done, total: n }),
      })
      if (key) setReport({ key, targets, outcomes })
    } finally {
      applyingRef.current = false
      setBusy(false)
      setProgress(null)
      // What is left to find has changed.
      setCountRun((n) => n + 1)
    }
  }

  async function startReplaceWide(): Promise<void> {
    if (!canEdit) return
    setBusy(true)
    try {
      const targets = await collectTargets(room, scope as 'artefact' | 'bid')
      setPendingTargets(targets)
    } finally {
      setBusy(false)
    }
  }

  function confirmReplaceWide(): void {
    const targets = pendingTargets
    setPendingTargets(null)
    if (targets) void replaceWide(targets)
  }

  // One row per artefact: the Replace all report, else the count (the open part's own count in it).
  const rows = shownReport
    ? sumByArtefact(shownReport.targets, shownReport.outcomes)
    : wide
      ? sumByArtefact(wide.targets, wide.outcomes, live)
      : null
  const openArtefactId = artefactOf(room)

  const counter =
    query === ''
      ? ''
      : isLocalScope
        ? matchCount === 0
          ? t('fr_no_matches')
          : t('fr_match_pos').replace('{i}', String(clampedIndex + 1)).replace('{n}', String(matchCount))
        : total === null
          ? ''
          : total === 0
            ? t('fr_no_matches')
            : t('fr_match_pos').replace('{i}', position === null ? '–' : String(position + 1)).replace('{n}', String(total))
  const canStep = isLocalScope || !counts ? matchCount > 0 : (total ?? 0) > 0 && !pendingHere

  return createPortal(
    <div
      className="fr-pop"
      role="dialog"
      aria-modal="true"
      aria-label={mode === 'replace' ? t('fr_title_replace') : t('fr_title_find')}
      onKeyDown={(e) => {
        if (e.key === 'Escape') close()
      }}
    >
      {/* "Find in [This slide ▾]": the scope reads as the end of the title (AB#330), and the
          visible verb is the dropdown's accessible name. */}
      <div className="fr-pop-head">
        <span className="fr-pop-title" id={titleId}>{mode === 'replace' ? t('fr_replace_in') : t('fr_find_in')}</span>
        <select className="fmtsel fr-scope" value={scope} onChange={(e) => setScope(e.target.value as FindScope)} aria-labelledby={titleId}>
          {isSlides ? (
            <option value="slide">{t('fr_scope_slide')}</option>
          ) : (
            <option value="section" disabled={isGrid}>{t('fr_scope_section')}</option>
          )}
          <option value="part" disabled={isGrid}>{t('scope_part')}</option>
          <option value="artefact">{t('scope_artefact')}</option>
          <option value="bid">{t('scope_bid')}</option>
        </select>
        <button className="modal-close" aria-label={t('close')} onClick={close}>✕</button>
      </div>

      <div className="fr-row">
        <input
          ref={inputRef}
          className="fr-input"
          type="text"
          placeholder={t('fr_find_ph')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return
            e.preventDefault()
            step(e.shiftKey ? -1 : 1)
          }}
        />
        <span className="fr-count" aria-live="polite">{counter}</span>
        <button className="fmtbtn" title={t('fr_prev')} aria-label={t('fr_prev')} disabled={!canStep} onClick={() => step(-1)}>↑</button>
        <button className="fmtbtn" title={t('fr_next')} aria-label={t('fr_next')} disabled={!canStep} onClick={() => step(1)}>↓</button>
      </div>

      {mode === 'replace' && canEdit && (
        <div className="fr-row">
          <input
            className="fr-input"
            type="text"
            placeholder={t('fr_replace_ph')}
            value={replacement}
            onChange={(e) => setReplacement(e.target.value)}
          />
          <button className="mini ghost" disabled={matchCount === 0} onClick={replaceOne}>{t('fr_replace_one')}</button>
          {isLocalScope ? (
            <button className="mini ok" disabled={matchCount === 0} onClick={replaceAllLocal}>{t('fr_replace_all')}</button>
          ) : (
            // Hidden (not merely disabled) while a confirmation is pending — a second click must
            // not queue a second collectTargets/confirm behind the first, and jsdom's a11y tree
            // would otherwise expose two same-named "Replace all" controls at once.
            !pendingTargets && (
              <button className="mini ok" disabled={busy || query === ''} onClick={() => void startReplaceWide()}>{t('fr_replace_all')}</button>
            )
          )}
        </div>
      )}

      <div className="fr-row fr-scope-row">
        <label className="fr-case">
          <input type="checkbox" checked={caseSensitive} onChange={(e) => setCaseSensitive(e.target.checked)} />
          {t('fr_case_sensitive')}
        </label>
      </div>

      {isGrid && isLocalScope && <p className="fr-notice">{t('fr_grid_unavailable')}</p>}
      {notice && <p className="fr-notice" role="alert">{notice}</p>}
      {progress && <p className="fr-notice">{t('fr_progress').replace('{done}', String(progress.done)).replace('{total}', String(progress.total))}</p>}

      {isWideScope && rows && (
        <div className="fr-results">
          <p className="fr-results-title">{t('fr_results_title')}</p>
          <ul>
            {rows.map((r) => {
              const current = r.artefact === openArtefactId
              return (
                <li key={r.artefact}>
                  <button
                    type="button"
                    className={'fr-result-row' + (current ? ' is-current' : '')}
                    aria-current={current ? 'true' : undefined}
                    onClick={() => openArtefact(r.artefact)}
                  >
                    <span className="fr-result-name">{r.title}</span>
                    <span className="fr-result-status">{sumLabel(t, r)}</span>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {pendingTargets && (
        <ConfirmModal
          title={t('fr_wide_confirm_title')}
          message={`${t('fr_wide_target_count').replace('{n}', String(pendingTargets.length))} — "${query}" → "${replacement}". ${t('fr_wide_confirm_msg')}`}
          confirmLabel={t('fr_replace_all')}
          onConfirm={confirmReplaceWide}
          onClose={() => setPendingTargets(null)}
        />
      )}
    </div>,
    document.body,
  )
}
